/**
 * `growth/regime` — **THE source of truth for ad hardening.**
 *
 * ## The invariant (a single one, holding across the entire kit)
 *
 * > Configuration **can only TIGHTEN the non-waivable minimums** of the
 * > regime. No channel — environment variable, back-office override,
 * > options from `AdProvider.init()` call, or runtime configuration
 * > key (`adcaps.*`) — can leave the regime more lenient than the **declared
 * > tier floor** ([`adTierFloor`]).
 *
 * Note scope: "non-waivable" is NOT "all caps". See §"THREE
 * natures" below — the part most easily mispropagated.
 *
 * ## Why this module exists (three holes, three layers, one pattern)
 *
 * The same bug has appeared three times in the kit, each in a different
 * layer, because each layer merged configuration in its own way:
 *
 * | # | Layer                   | How the regime was relaxed                                 | Discovered by    |
 * |---|-------------------------|------------------------------------------------------------|------------------|
 * | 1 | `resolveAdsConfig`      | `ADS_CHILD_DIRECTED=false` disabled child treatment in a tier K | `kid-games-app` |
 * | 2 | `WebAdProvider.init`    | `init({ childDirected: false })` undid what was clamped    | canon (listto)   |
 * | 3 | `adGate`                | `adcaps.interstitial_per_day = 3` overwrote child cap of 2 via `input.policy` | `habit-streak-app` |
 *
 * All three fixes were correct and all three were LOCAL: a clamp here, a
 * `harden` there, a strict merge further out. As long as rule lives scattered,
 * a new layer is born without it and hole number four is a matter of time.
 *
 * This is why there is **a single composition function**, [`hardenAdRegime`], and
 * each channel is a trivial ADAPTER calling it:
 *
 * ```text
 *   env  ─┐
 *   admin ├─► resolveAdsConfig → clampAdsConfigToTier ─┐
 *   init ─┴─► hardenAdInitOptions ────────────────────┤
 *   adcaps.* (runtime config) → adCapPolicyFromSettings├─► hardenAdRegime ─► effective regime
 *   placement registry (code) ───► input.policy ───────┘        ▲
 *                                                     tier floor (AD_TIER_REGIMES
 *                                                       + AD_TIER_CAP_FLOORS)
 * ```
 *
 * Adding a fifth channel tomorrow means writing a 3-line adapter; what you
 * **cannot** do is merge configuration on your own, because the rest of
 * the kit exposes no other path to the regime.
 *
 * The regime covers BOTH halves previously separated: policy *flags*
 * (ads yes/no, child treatment, rating ceiling) and numeric *caps*
 * for capping ([`AdCapPolicy`]).
 *
 * ## ⚠️ THREE natures, not one — classification is the delicate part
 *
 * "Only harden" is correct as a principle and **disastrous if applied to
 * all caps alike": leaves unreachable parameters that exist
 * precisely to move, and config-over-code that only allows tightening is not
 * configuration, it is a brake. `AdCapPolicy` caps are NOT homogeneous:
 *
 * 1. **Non-waivable minimums** — [`AD_TIER_CAP_FLOORS`] and tier regime.
 *    LAW or app store promise (COPPA, Play Families, "zero ads" of
 *    B2B/privacy tiers). Only tightened, fail-closed, and **no channel touches them**.
 *    It is over THIS set — and only over it — that property
 *    "no combination produces a more lenient regime" is asserted.
 *
 * 2. **Operational parameters** — everything else in [`AdCapPolicy`]:
 *    `minGapBetweenInterstitialsMs`, `interruptionBudgetMs`, `minUnitsCompleted`,
 *    `allowInterstitialOnFirstSession`, `ignoredDaysBeforeBackoff`,
 *    `offersPerDayWhenBackedOff`, `nativePerScreen`, `bannersVisible`. Move in
 *    **both directions**: an operator may want more distance between
 *    ads *or* less, and both are legitimate decisions. Non-waivable floor
 *    bounds them **only when tier declares it** (in child regime yes, in
 *    tier A no: its floor is NEUTRAL, see [`NO_AD_CAP_FLOOR`]).
 *
 * 3. **Product ceiling** — [`PORTFOLIO_AD_CAP_CEILING`], covering EXACTLY
 *    the four keys of `adcaps.*`. Unidirectional **by decision of
 *    portfolio**, not law: product listing promises a maximum of
 *    interruptions and configuration cannot raise it. Hence declared
 *    separately and is **parameterizable per app** — a clone with another promise passes its
 *    own ceiling instead of bifurcating the kit.
 *
 * Confusing (2) with (1) is the mistake to avoid when propagating this: a clone
 * that applied strict merge to ENTIRE set left `interruptionBudgetMs`
 * unreachable and broke the canon interruption budget test.
 */

import type { AdCapPolicy, AdContentRating, AdTier, AdTierRegime } from "./types";
import { AD_CONTENT_RATINGS, AD_TIER_REGIMES, AD_TIERS, STRICTEST_AD_TIER_REGIME } from "./types";

/** Is this string a DECLARED tier of the portfolio? (narrowing guard). */
export function isAdTier(value: string): value is AdTier {
  return (AD_TIERS as readonly string[]).includes(value);
}

const MINUTE_MS = 60_000;

// ── Portfolio caps ──────────────────────────────────────────────────

/**
 * Portfolio defaults (§4 of ads strategy): behavior with which
 * any portfolio app starts.
 *
 * **Not a floor**: most of these caps are operational parameters and move
 * in both directions. Only the four keys of
 * [`PORTFOLIO_AD_CAP_CEILING`] use this value also as CEILING, and by
 * commercial decision, not law.
 */
export const DEFAULT_AD_CAP_POLICY: AdCapPolicy = {
  interstitialPerSession: 1,
  interstitialPerDay: 3,
  minGapBetweenInterstitialsMs: 10 * MINUTE_MS,
  allowInterstitialOnFirstSession: false,
  minUnitsCompleted: 1,
  rewardsPerDayPerType: 3,
  offersPerDayPerPlacement: 2,
  ignoredDaysBeforeBackoff: 3,
  offersPerDayWhenBackedOff: 1,
  nativePerScreen: 1,
  bannersVisible: 1,
  interruptionBudgetMs: 10 * MINUTE_MS,
};

/**
 * Child regime (COPPA / Play Families / App Store Kids): STRICTER than the
 * general policy. Interstitials only live `between-screens` (never inside a
 * game), max 1/session and 2/day; native is not used; rewarded requires an
 * adult gate and is disabled by default.
 */
export const CHILD_AD_CAP_POLICY: AdCapPolicy = {
  ...DEFAULT_AD_CAP_POLICY,
  interstitialPerSession: 1,
  interstitialPerDay: 2,
  rewardsPerDayPerType: 1,
};

/**
 * **Neutral element** of [`strictestAdCapPolicy`]: a floor requiring nothing.
 *
 * What makes the mechanism uniform. A tier without its own capping policy
 * (tier A, adult consumption) is not treated as a special case with an `if`: declares
 * this floor and SAME strict merge applies, yielding identity.
 * No two code paths — therefore none is forgotten.
 */
export const NO_AD_CAP_FLOOR: AdCapPolicy = {
  interstitialPerSession: Number.POSITIVE_INFINITY,
  interstitialPerDay: Number.POSITIVE_INFINITY,
  minGapBetweenInterstitialsMs: 0,
  allowInterstitialOnFirstSession: true,
  minUnitsCompleted: 0,
  rewardsPerDayPerType: Number.POSITIVE_INFINITY,
  offersPerDayPerPlacement: Number.POSITIVE_INFINITY,
  ignoredDaysBeforeBackoff: Number.POSITIVE_INFINITY,
  offersPerDayWhenBackedOff: Number.POSITIVE_INFINITY,
  nativePerScreen: Number.POSITIVE_INFINITY,
  bannersVisible: Number.POSITIVE_INFINITY,
  interruptionBudgetMs: 0,
};

/**
 * MAXIMUM floor: zero impressions of any format. What no-ad tiers
 * and **any unknown tier** receive (fail-closed, just like
 * [`STRICTEST_AD_TIER_REGIME`]): if someone adds a tier and forgets to declare its
 * capping, app stops showing ads instead of showing them without policy.
 *
 * Finite numbers on purpose: `Infinity` in a multiplicative cap produces
 * `NaN` when the counter is 0 (`0 * Infinity`), and a `NaN` in a comparison
 * is always `false` — that is, "allow". Fail-closed does not allow `NaN`.
 */
export const STRICTEST_AD_CAP_FLOOR: AdCapPolicy = {
  interstitialPerSession: 0,
  interstitialPerDay: 0,
  minGapBetweenInterstitialsMs: Number.MAX_SAFE_INTEGER,
  allowInterstitialOnFirstSession: false,
  minUnitsCompleted: Number.MAX_SAFE_INTEGER,
  rewardsPerDayPerType: 0,
  offersPerDayPerPlacement: 0,
  ignoredDaysBeforeBackoff: 0,
  offersPerDayWhenBackedOff: 0,
  nativePerScreen: 0,
  bannersVisible: 0,
  interruptionBudgetMs: Number.MAX_SAFE_INTEGER,
};

/**
 * **Nature 1 — NON-WAIVABLE minimums per tier** (the numeric half of
 * `AD_TIER_REGIMES`, which only covers flags).
 *
 * - `A` — adult consumption: **no non-waivable minimum** => [`NO_AD_CAP_FLOOR`].
 *   All its caps are operational parameters and move in both directions.
 * - `K` — kids: [`CHILD_AD_CAP_POLICY`] ALWAYS, even if app does not mark the
 *   user as minor: in a child tier EVERY user is one. Here it is law.
 * - `C`/`D` — zero ads: maximum floor, in case anyone mounts gate anyway.
 *
 * This table is the ONLY thing against which "no channel can relax" is asserted.
 */
export const AD_TIER_CAP_FLOORS: Readonly<Record<AdTier, AdCapPolicy>> = {
  A: NO_AD_CAP_FLOOR,
  K: CHILD_AD_CAP_POLICY,
  C: STRICTEST_AD_CAP_FLOOR,
  D: STRICTEST_AD_CAP_FLOOR,
};

/**
 * Keys that runtime configuration (`adcaps.*`) can move. The rest of
 * `AdCapPolicy` is **code**: tuned in the repo, not from an admin panel.
 */
export const CONFIGURABLE_AD_CAP_KEYS = [
  "interstitialPerSession",
  "interstitialPerDay",
  "rewardsPerDayPerType",
  "offersPerDayPerPlacement",
] as const;

export type ConfigurableAdCapKey = (typeof CONFIGURABLE_AD_CAP_KEYS)[number];

/**
 * **Nature 3 — PRODUCT ceiling** of the portfolio (§4 of strategy).
 *
 * Not a legal floor: commercial promise of "at most you are interrupted
 * this much". Unidirectional **by decision** (lower yes, raise no) and
 * lives apart from [`AD_TIER_CAP_FLOORS`], which is law.
 *
 * Covers EXACTLY the four `adcaps.*` keys and nothing else: operational
 * parameters have no ceiling, and code injection does not pass here either.
 *
 * A clone whose listing promises something else **does not fork the kit**: passes its own
 * ceiling to `adCapPolicyFromSettings` (and adjusts server clamp, which is its
 * mirror in `app_config::resolve::clamp`).
 */
export const PORTFOLIO_AD_CAP_CEILING: Readonly<Record<ConfigurableAdCapKey, number>> = {
  interstitialPerSession: DEFAULT_AD_CAP_POLICY.interstitialPerSession,
  interstitialPerDay: DEFAULT_AD_CAP_POLICY.interstitialPerDay,
  rewardsPerDayPerType: DEFAULT_AD_CAP_POLICY.rewardsPerDayPerType,
  offersPerDayPerPlacement: DEFAULT_AD_CAP_POLICY.offersPerDayPerPlacement,
};

// ── Strict fusion ──────────────────────────────────────────────────────────

/**
 * The STRICTEST of two ratings. `AD_CONTENT_RATINGS` is ordered from most
 * strict to most lenient, so "strictest" = lower index.
 */
export function strictestAdContentRating(a: AdContentRating, b: AdContentRating): AdContentRating {
  return AD_CONTENT_RATINGS.indexOf(a) <= AD_CONTENT_RATINGS.indexOf(b) ? a : b;
}

/**
 * NUMERIC caps of the policy (all except the single boolean).
 *
 * `Exclude` instead of a manual list: if a cap is added tomorrow to
 * `AdCapPolicy`, it enters here alone and [`STRICTER_DIRECTION`] stops compiling until
 * someone declares whether stricter is minimum or maximum.
 */
export type NumericAdCapKey = Exclude<keyof AdCapPolicy, "allowInterstitialOnFirstSession">;

type NumericCapKey = NumericAdCapKey;

/**
 * Direction of "stricter" for each numeric cap.
 *
 * `"min"` = a lower number is less annoying (impression caps);
 * `"max"` = a higher number is less annoying, so stricter is the larger one
 * (minimum gaps, interruption budget, required value units).
 *
 * The `Record<NumericCapKey, …>` is an exhaustiveness guard: a cap cannot
 * be added without classifying its direction.
 */
const STRICTER_DIRECTION: Readonly<Record<NumericCapKey, "min" | "max">> = {
  interstitialPerSession: "min",
  interstitialPerDay: "min",
  minGapBetweenInterstitialsMs: "max",
  minUnitsCompleted: "max",
  rewardsPerDayPerType: "min",
  offersPerDayPerPlacement: "min",
  ignoredDaysBeforeBackoff: "min",
  offersPerDayWhenBackedOff: "min",
  nativePerScreen: "min",
  bannersVisible: "min",
  interruptionBudgetMs: "max",
};

/**
 * The STRICTEST of two policies, key by key.
 *
 * Same principle as tier regime clamp: **classification beats the
 * flag**. Exists because gate `input.policy` stopped being just a code
 * injection — since CONFIG_OVER_CODE it brings `adcaps.*` an admin edits
 * hot, and without this `adcaps.interstitial_per_day = 3` would have RELAXED the
 * kids cap of 2 from back-office.
 *
 * The complete explicit object is built instead of iterating and assigning:
 * the return type itself forces every key to be covered (forgetting one
 * does not compile), and no type assertion is needed.
 *
 * Commutative, associative and idempotent: lattice infimum, with
 * [`NO_AD_CAP_FLOOR`] as neutral element.
 */
export function strictestAdCapPolicy(a: AdCapPolicy, b: AdCapPolicy): AdCapPolicy {
  const pick = (key: NumericCapKey): number =>
    STRICTER_DIRECTION[key] === "min" ? Math.min(a[key], b[key]) : Math.max(a[key], b[key]);
  return {
    interstitialPerSession: pick("interstitialPerSession"),
    interstitialPerDay: pick("interstitialPerDay"),
    minGapBetweenInterstitialsMs: pick("minGapBetweenInterstitialsMs"),
    // The only boolean: `false` (not allowed in the 1st session) is the stricter one.
    allowInterstitialOnFirstSession:
      a.allowInterstitialOnFirstSession && b.allowInterstitialOnFirstSession,
    minUnitsCompleted: pick("minUnitsCompleted"),
    rewardsPerDayPerType: pick("rewardsPerDayPerType"),
    offersPerDayPerPlacement: pick("offersPerDayPerPlacement"),
    ignoredDaysBeforeBackoff: pick("ignoredDaysBeforeBackoff"),
    offersPerDayWhenBackedOff: pick("offersPerDayWhenBackedOff"),
    nativePerScreen: pick("nativePerScreen"),
    bannersVisible: pick("bannersVisible"),
    interruptionBudgetMs: pick("interruptionBudgetMs"),
  };
}

// ── The regime and its composition ──────────────────────────────────────────────

/**
 * COMPLETE ad regime: policy flags and numeric caps.
 * As a *floor* it is guaranteed minimum; as *result* it is what governs.
 */
export interface AdRegime extends AdTierRegime {
  /** Capping limits: most lenient allowed. */
  readonly caps: AdCapPolicy;
}

/**
 * What an arbitrary configuration channel REQUESTS. All optional: each
 * adapter fills what its channel can express and stays silent on the rest.
 *
 * Careful with semantics, which is half of the invariant:
 *   - `adsAllowed: false` TURNS OFF (respected); `true` does not enable what the
 *     floor did not allow already;
 *   - `childDirected: true` TIGHTENS (respected); `false` does not disable anything;
 *   - `maxAdContentRating` only decreases;
 *   - `caps` folds with [`strictestAdCapPolicy`], never with a spread.
 */
export interface AdRegimeRequest {
  adsAllowed?: boolean;
  childDirected?: boolean;
  maxAdContentRating?: AdContentRating;
  caps?: Partial<AdCapPolicy>;
}

/**
 * Declared regime (flags) of a tier. An unknown tier falls back to STRICTEST
 * regime (fail-closed): without a declared policy, no advertising is served.
 */
export function adTierRegime(tier: string): AdTierRegime {
  return isAdTier(tier) ? AD_TIER_REGIMES[tier] : STRICTEST_AD_TIER_REGIME;
}

/**
 * **Complete floor of a tier**: flags + caps. The only legitimate origin of
 * "declared minimum" against which everything else is tightened.
 *
 * Absent `tier` = NEUTRAL floor (tier `A`). Not an exception to
 * fail-closed: tier already comes clamped by `clampAdsConfigToTier` before
 * reaching here, and omitting it in a gate call means
 * "I trust config", not "no tier". A present but CORRUPT tier
 * (string not in `AD_TIERS`) does fall to maximum floor.
 */
export function adTierFloor(tier?: string): AdRegime {
  if (tier === undefined) {
    return { ...AD_TIER_REGIMES.A, caps: AD_TIER_CAP_FLOORS.A };
  }
  return {
    ...adTierRegime(tier),
    caps: isAdTier(tier) ? AD_TIER_CAP_FLOORS[tier] : STRICTEST_AD_CAP_FLOOR,
  };
}

/**
 * **THE function.** Composes a floor with what a channel requests and returns
 * effective regime. Monotonic (only tightens), idempotent and total: no
 * input produces a more lenient regime than `floor`.
 *
 * Kids regime ALWAYS drags its two consequences, wherever it comes
 * from (tier or request tightening an adult tier): rating `G` and
 * caps of [`CHILD_AD_CAP_POLICY`]. That consistency applies here, once,
 * and not at each ad-hoc place.
 */
export function hardenAdRegime(floor: AdRegime, request: AdRegimeRequest = {}): AdRegime {
  // Can only TURN OFF: a `true` does not revive a tier without ads.
  const adsAllowed = floor.adsAllowed && (request.adsAllowed ?? true);
  // Can only TURN ON: a `false` does not disable the minor regime.
  const childDirected = floor.childDirected || request.childDirected === true;

  let maxAdContentRating = strictestAdContentRating(
    floor.maxAdContentRating,
    request.maxAdContentRating ?? floor.maxAdContentRating,
  );
  if (childDirected) {
    maxAdContentRating = strictestAdContentRating(maxAdContentRating, "G");
  }

  // Base is chosen by REGIME, not caller: requesting capping with defaults
  // in kids regime starts from child policy, not general policy.
  const base = childDirected ? CHILD_AD_CAP_POLICY : DEFAULT_AD_CAP_POLICY;
  let caps = strictestAdCapPolicy(floor.caps, { ...base, ...request.caps });
  if (childDirected) {
    caps = strictestAdCapPolicy(caps, CHILD_AD_CAP_POLICY);
  }

  return { adsAllowed, childDirected, maxAdContentRating, caps };
}

/**
 * Applies **product ceiling** (nature 3) to what a configuration
 * channel requests. Only touches keys ceiling declares; others pass as
 * is, because they are operational parameters without a ceiling.
 *
 * Reuses [`strictestAdCapPolicy`] instead of ad-hoc `Math.min` so that
 * "stricter" direction lives in ONE place: if a configurable key
 * changed direction tomorrow, this helper follows it transparently.
 */
export type AdCapCeiling = Readonly<Partial<Record<NumericAdCapKey, number>>>;

/**
 * Is this key a declared NUMERIC cap? Queries `STRICTER_DIRECTION`
 * to avoid maintaining a duplicate list: if a cap is added, it enters automatically.
 */
function isNumericAdCapKey(key: string): key is NumericAdCapKey {
  return Object.hasOwn(STRICTER_DIRECTION, key);
}

export function capToCeiling(
  requested: Partial<AdCapPolicy>,
  ceiling: AdCapCeiling = PORTFOLIO_AD_CAP_CEILING,
): Partial<AdCapPolicy> {
  const merged = strictestAdCapPolicy(
    { ...DEFAULT_AD_CAP_POLICY, ...ceiling },
    { ...DEFAULT_AD_CAP_POLICY, ...requested },
  );
  const out: Partial<AdCapPolicy> = { ...requested };
  for (const key of Object.keys(ceiling)) {
    if (isNumericAdCapKey(key) && requested[key] !== undefined) {
      Object.assign(out, { [key]: merged[key] });
    }
  }
  return out;
}

/**
 * EFFECTIVE capping policy for a gate decision.
 *
 * Adapter for "caps" channel: receives requested config (runtime `adcaps.*` and/or
 * code injection from placement registry) and tightens against tier floor
 * and minor regime.
 */
export function effectiveAdCapPolicy(input: {
  /** App tier (from `AdsConfig`, already clamped). */
  tier?: string;
  /** Does minor regime apply to THIS user? */
  childDirected?: boolean;
  /**
   * Caps requested by config and/or code. **Operational** arrive here as
   * is and are respected in both directions: only non-waivable tier
   * floor (nature 1) can trim them.
   */
  requested?: Partial<AdCapPolicy>;
}): AdCapPolicy {
  return hardenAdRegime(adTierFloor(input.tier), {
    childDirected: input.childDirected,
    caps: input.requested,
  }).caps;
}
